import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as contractsApi from '../../api/contracts'
import * as trainersApi from '../../api/trainers'
import * as teamsApi from '../../api/teams'
import * as paymentsApi from '../../api/payments'
import { settingsApi } from '../../api/settings'
import { useUIStore } from '../../stores/uiStore'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Spinner from '../../components/common/Spinner'
import ManualPaymentScheduleEditor from '../../components/contracts/ManualPaymentScheduleEditor'
import PaymentSchedulePreview from '../../components/contracts/PaymentSchedulePreview'
import { calculatePayments } from '../../utils/paymentCalculator'
import { getSeasonDates } from '../../utils/seasonUtils'
import { DocumentArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline'

/**
 * Contract Create Page
 * Form to create a new contract with file upload, trainer and team linking
 */
export default function ContractCreate() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  
  const [formData, setFormData] = useState({
    title: '',
    start_date: '',
    end_date: '',
    total_amount: '',
    payment_frequency: 'twice-yearly',
    payment_day_of_month: '25',
  })
  
  const [selectedFile, setSelectedFile] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [selectedTrainers, setSelectedTrainers] = useState([])
  const [selectedTeams, setSelectedTeams] = useState([])
  const [manualPayments, setManualPayments] = useState([])
  
  // Fetch trainers and teams for selection
  const { data: trainersData, isLoading: loadingTrainers } = useQuery({
    queryKey: ['trainers-list'],
    queryFn: () => trainersApi.getTrainers({ limit: 1000 }),
  })
  
  const { data: teamsData, isLoading: loadingTeams } = useQuery({
    queryKey: ['teams-list'],
    queryFn: () => teamsApi.getTeams({ limit: 1000 }),
  })
  
  const trainers = trainersData?.data || []
  const teams = teamsData?.data || []

  const createMutation = useMutation({
    mutationFn: ({ data, file }) => contractsApi.createContract(data, file),
    // Don't use onSuccess/onError - we handle everything in handleSubmit
  })
  
  // Load volleyball season settings and auto-populate dates
  useEffect(() => {
    const loadSeasonDates = async () => {
      try {
        const response = await settingsApi.getByCategory('application')
        const volleyballSeason = response.data?.volleyball_season
        
        if (volleyballSeason) {
          const seasonDates = getSeasonDates(volleyballSeason)
          if (seasonDates) {
            setFormData(prev => ({
              ...prev,
              start_date: seasonDates.start_date,
              end_date: seasonDates.end_date
            }))
          }
        }
      } catch (error) {
        // Silently fail if settings not available
        console.warn('Could not load volleyball season settings:', error)
      }
    }
    
    loadSeasonDates()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validate manual payments if in manual mode
    if (formData.payment_frequency === 'manual') {
      if (manualPayments.length === 0) {
        showToast('Please add at least one payment', 'error')
        return
      }
      
      // Check if all payments have dates
      const hasEmptyDates = manualPayments.some(p => !p.scheduled_date)
      if (hasEmptyDates) {
        showToast('All payments must have a date', 'error')
        return
      }
      
      // Validate total matches contract amount
      const totalPaymentAmount = manualPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
      const contractAmount = parseFloat(formData.total_amount)
      const difference = Math.abs(totalPaymentAmount - contractAmount)
      
      if (difference > 0.01) {
        showToast('Payment total must match contract amount exactly', 'error')
        return
      }
    }
    
    // Prepare data with selected trainer and team IDs
    const data = {
      ...formData,
      trainer_ids: selectedTrainers.map(t => t.id),
      team_ids: selectedTeams.map(t => t.id),
    }
    
    try {
      // Create contract first
      const result = await createMutation.mutateAsync({ data, file: selectedFile })
      
      // If manual payments, create them separately BEFORE navigating away
      if (formData.payment_frequency === 'manual' && manualPayments.length > 0) {
        // Strip is_manual_amount before sending to backend
        const cleanedPayments = manualPayments.map(p => ({
          payment_number: p.payment_number,
          scheduled_date: p.scheduled_date,
          amount: p.amount,
          status: 'scheduled'
        }))
        
        // IMPORTANT: await this call before proceeding
        await paymentsApi.createManualPayments(result.id, cleanedPayments)
      }
      
      // Only invalidate and navigate AFTER all operations complete
      queryClient.invalidateQueries(['contracts'])
      queryClient.invalidateQueries(['dashboard-stats'])
      showToast('Contract created successfully!', 'success')
      navigate('/contracts')
    } catch (error) {
      showToast(error.response?.data?.error || error.message || 'Failed to create contract', 'error')
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }
  
  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      if (!allowedTypes.includes(file.type)) {
        showToast('Please select a PDF or Word document', 'error')
        return
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        showToast('File size must be less than 10MB', 'error')
        return
      }
      
      setSelectedFile(file)
    }
  }
  
  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }
  
  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    // Only reset if leaving the drop zone entirely
    if (e.currentTarget === e.target) {
      setIsDragging(false)
    }
  }
  
  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }
  
  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    
    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const file = files[0]
      
      // Reuse existing validation logic from handleFileChange
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      if (!allowedTypes.includes(file.type)) {
        showToast('Please select a PDF or Word document', 'error')
        return
      }
      
      if (file.size > 10 * 1024 * 1024) {
        showToast('File size must be less than 10MB', 'error')
        return
      }
      
      setSelectedFile(file)
      showToast('File ready for upload', 'success')
    }
  }
  
  const removeFile = () => {
    setSelectedFile(null)
  }
  
  const toggleTrainerSelection = (trainer) => {
    setSelectedTrainers(prev => {
      const exists = prev.find(t => t.id === trainer.id)
      if (exists) {
        return prev.filter(t => t.id !== trainer.id)
      }
      return [...prev, trainer]
    })
  }
  
  const toggleTeamSelection = (team) => {
    setSelectedTeams(prev => {
      const exists = prev.find(t => t.id === team.id)
      if (exists) {
        return prev.filter(t => t.id !== team.id)
      }
      return [...prev, team]
    })
  }
  
  const removeTrainer = (trainerId) => {
    setSelectedTrainers(prev => prev.filter(t => t.id !== trainerId))
  }
  
  const removeTeam = (teamId) => {
    setSelectedTeams(prev => prev.filter(t => t.id !== teamId))
  }

  if (loadingTrainers || loadingTeams) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Create Contract</h1>
        <p className="mt-2 text-gray-600">Fill in the details to create a new contract</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <Card.Header>
            <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4">
              <Input
                label="Contract Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="e.g., Head Coach Contract 2024"
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Start Date"
                  name="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={handleChange}
                  required
                />

                <Input
                  label="End Date"
                  name="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Total Amount (€)"
                  name="total_amount"
                  type="number"
                  value={formData.total_amount}
                  onChange={handleChange}
                  placeholder="5000"
                />

                <Input.Select
                  label="Payment Frequency"
                  name="payment_frequency"
                  value={formData.payment_frequency}
                  onChange={handleChange}
                >
                  <option value="one-time">One-time (upon contract start)</option>
                  <option value="twice">Twice per contract period</option>
                  <option value="twice-yearly">Twice Yearly (May & December)</option>
                  <option value="quarterly">Quarterly (4 payments if ≤1yr, else calendar quarters)</option>
                  <option value="monthly">Monthly</option>
                  <option value="manual">Manual Payment Schedule</option>
                </Input.Select>
              </div>

              {formData.payment_frequency !== 'manual' && (
                <Input
                  label="Payment Day of Month"
                  name="payment_day_of_month"
                  type="number"
                  min="1"
                  max="31"
                  value={formData.payment_day_of_month}
                  onChange={handleChange}
                  helpText="Day of month for recurring payments (1-31). Automatically adjusted for shorter months."
                />
              )}
            </div>
          </Card.Body>
        </Card>

        {/* File Upload */}
        <Card>
          <Card.Header>
            <h2 className="text-lg font-semibold text-gray-900">Contract Document</h2>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4">
              {!selectedFile ? (
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-300 hover:border-primary-500'
                  }`}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <DocumentArrowUpIcon className={`mx-auto h-12 w-12 ${isDragging ? 'text-primary-600' : 'text-gray-400'}`} />
                  <div className="mt-4">
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <span className="text-primary-600 hover:text-primary-700 font-medium">
                        Upload a file
                      </span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="sr-only"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                      />
                    </label>
                    <span className="text-gray-500"> or drag and drop</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    PDF or Word document up to 10MB
                  </p>
                  {isDragging && (
                    <p className="text-sm text-primary-600 font-medium mt-2">
                      Drop file here
                    </p>
                  )}
                </div>
              ) : (
                <div className="border border-gray-300 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <DocumentArrowUpIcon className="h-8 w-8 text-primary-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>

        {/* Payment Schedule Preview/Editor */}
        {formData.total_amount && formData.start_date && formData.end_date && (
          formData.payment_frequency === 'manual' ? (
            <ManualPaymentScheduleEditor
              totalAmount={parseFloat(formData.total_amount) || 0}
              payments={manualPayments}
              onChange={setManualPayments}
              startDate={formData.start_date}
              endDate={formData.end_date}
            />
          ) : (
            <Card>
              <Card.Header>
                <h2 className="text-lg font-semibold text-gray-900">Payment Schedule Preview</h2>
              </Card.Header>
              <Card.Body>
                <PaymentSchedulePreview
                  payments={calculatePayments(formData)}
                  isPreview={true}
                />
              </Card.Body>
            </Card>
          )
        )}

        {/* Link Trainers & Teams - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Link Trainers */}
          <Card>
            <Card.Header>
              <h2 className="text-lg font-semibold text-gray-900">Link Trainers</h2>
              <p className="text-sm text-gray-600">Select trainers to associate with this contract</p>
            </Card.Header>
            <Card.Body>
              <div className="space-y-4">
                {/* Selected Trainers */}
                {selectedTrainers.length > 0 && (
                  <div className="flex flex-wrap gap-2 pb-4 border-b">
                    {selectedTrainers.map((trainer) => (
                      <span
                        key={trainer.id}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm"
                      >
                        {`${trainer.first_name} ${trainer.last_name}`}
                        <button
                          type="button"
                          onClick={() => removeTrainer(trainer.id)}
                          className="hover:text-primary-900"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Available Trainers */}
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {trainers.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No trainers available</p>
                  ) : (
                    trainers.map((trainer) => {
                      const isSelected = selectedTrainers.find(t => t.id === trainer.id)
                      return (
                        <label
                          key={trainer.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            isSelected
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={() => toggleTrainerSelection(trainer)}
                            className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{`${trainer.first_name} ${trainer.last_name}`}</p>
                            <p className="text-xs text-gray-500">{trainer.email}</p>
                          </div>
                        </label>
                      )
                    })
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Link Teams */}
          <Card>
            <Card.Header>
              <h2 className="text-lg font-semibold text-gray-900">Link Teams</h2>
              <p className="text-sm text-gray-600">Select teams to associate with this contract</p>
            </Card.Header>
            <Card.Body>
              <div className="space-y-4">
                {/* Selected Teams */}
                {selectedTeams.length > 0 && (
                  <div className="flex flex-wrap gap-2 pb-4 border-b">
                    {selectedTeams.map((team) => (
                      <span
                        key={team.id}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                      >
                        {team.team_name}
                        <button
                          type="button"
                          onClick={() => removeTeam(team.id)}
                          className="hover:text-blue-900"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Available Teams */}
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {teams.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No teams available</p>
                  ) : (
                    teams.map((team) => {
                      const isSelected = selectedTeams.find(t => t.id === team.id)
                      return (
                        <label
                          key={team.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={() => toggleTeamSelection(team)}
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{team.team_name}</p>
                            {team.category && (
                              <p className="text-xs text-gray-500">Category: {team.category}</p>
                            )}
                          </div>
                        </label>
                      )
                    })
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/contracts')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={createMutation.isPending}
          >
            Create Contract
          </Button>
        </div>
      </form>
    </div>
  )
}